import React, { useEffect, useState } from "react";
import { Link2, Link2Off, PlugZap, RefreshCw, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import LocationPicker from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

// Fallback tampilan saja bila akun Biteship belum pernah diuji (belum ada daftar kurir akun). Sumber utama = kurir akun Biteship tenant.
const KNOWN_COURIERS = ["sicepat", "jne", "jnt", "anteraja", "ninja", "tiki", "pos", "idexpress", "lion", "sap", "wahana", "gojek", "grab"];
const CAP_LABEL = { pickup: "Pickup", drop_off: "Drop Off" };

export const courierChoices = ({ accountCodes = [], liveCodes = [], saved = [] }) => {
  const account = [...new Set([...accountCodes, ...liveCodes].filter(Boolean))];
  const base = account.length ? account : KNOWN_COURIERS;
  return { account, choices: [...new Set([...base, ...saved.filter(Boolean)])].sort() };
};
const EMPTY_ORIGIN = { name: "", phone: "", email: "", address: "", postal_code: "", coordinate: "", note: "" };

export const errMsg = (e, fallback = "Terjadi kesalahan") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  return fallback;
};

export const CredentialCard = ({ cfg, onSaved }) => {
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const save = async () => {
    if (!apiKey.trim()) return toast.error("Masukkan API key Biteship milik toko Anda.");
    setBusy(true);
    try { const { data } = await api.put("/admin/biteship/settings", { api_key: apiKey }); setApiKey(""); onSaved(data); toast.success("API key tersimpan (terenkripsi)."); }
    catch (e) { toast.error(errMsg(e, "Gagal menyimpan API key")); } finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/biteship/test-connection");
      data.ok ? toast.success(`Terhubung ke Biteship (${data.courier_count} kurir).`) : toast.error(data.message || "Koneksi gagal");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Uji koneksi gagal")); } finally { setTesting(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Putuskan koneksi Biteship? Kredensial akan dihapus dari toko ini.")) return;
    try { const { data } = await api.delete("/admin/biteship/settings"); onSaved(data); toast.success("Koneksi Biteship diputus."); }
    catch (e) { toast.error(errMsg(e, "Gagal memutus koneksi")); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="biteship-credential-card">
      <div className="flex items-center gap-2 font-semibold"><PlugZap className="h-4 w-4" /> Akun Biteship (milik toko)</div>
      <p className="text-sm text-muted-foreground">Gunakan API key dari dashboard Biteship toko Anda sendiri. Saldo dan tagihan pengiriman berada di akun Biteship Anda. Key disimpan terenkripsi dan tidak ditampilkan kembali.</p>
      <div className="text-sm" data-testid="biteship-connection-status">
        Status: <span className="font-medium">{cfg.configured ? (cfg.connection_status || "tersimpan") : "belum dikonfigurasi"}</span>
        {cfg.api_key_masked && <span className="ml-2 font-mono text-xs" data-testid="biteship-api-key-masked">{cfg.api_key_masked}</span>}
        {cfg.environment && <span className="ml-2 text-xs uppercase rounded px-1.5 py-0.5 bg-muted">{cfg.environment}</span>}
      </div>
      <div className="space-y-1">
        <Label htmlFor="biteship-api-key">{cfg.configured ? "Ganti API key" : "API key"}</Label>
        <Input id="biteship-api-key" type="password" autoComplete="off" placeholder="biteship_live.xxxxx atau biteship_test.xxxxx" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="biteship-api-key-input" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={busy} data-testid="biteship-save-key-btn">Simpan API Key</Button>
        <Button variant="outline" onClick={test} disabled={testing || !cfg.configured} data-testid="biteship-test-connection-btn"><Link2 className="h-4 w-4 mr-1" />Uji Koneksi</Button>
        {cfg.configured && <Button variant="ghost" onClick={disconnect} data-testid="biteship-disconnect-btn"><Link2Off className="h-4 w-4 mr-1" />Putuskan</Button>}
      </div>
    </Card>
  );
};

export const OriginCard = ({ cfg, onSaved }) => {
  const [origin, setOrigin] = useState({ ...EMPTY_ORIGIN, ...(cfg.origin || {}) });
  const [couriers, setCouriers] = useState(cfg.couriers || []);
  const [servicesDisabled, setServicesDisabled] = useState(cfg.services_disabled || {});
  const [services, setServices] = useState(null); // dari GET /admin/biteship/couriers (kode layanan Biteship aktual)
  const [enabled, setEnabled] = useState(!!cfg.enabled);
  const [codEnabled, setCodEnabled] = useState(!!cfg.cod_enabled);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => { setOrigin({ ...EMPTY_ORIGIN, ...(cfg.origin || {}) }); setCouriers(cfg.couriers || []); setServicesDisabled(cfg.services_disabled || {}); setEnabled(!!cfg.enabled); setCodEnabled(!!cfg.cod_enabled); }, [cfg]);
  useEffect(() => {
    if (!cfg.configured) return;
    api.get("/admin/biteship/couriers").then((r) => setServices(r.data?.couriers || [])).catch(() => setServices([]));
  }, [cfg.configured, cfg.last_tested_at]);
  const servicesOf = (c) => (services || []).filter((x) => x.courier_code === c && x.courier_service_code);
  const { account, choices } = courierChoices({ accountCodes: cfg.available_courier_codes, liveCodes: (services || []).map((x) => x.courier_code), saved: couriers });
  const refreshCouriers = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/biteship/test-connection"); // endpoint existing: sinkron daftar kurir akun (read-only)
      data.ok ? toast.success(`Daftar kurir diperbarui (${data.courier_count} kurir di akun Biteship).`) : toast.error(data.message || "Gagal menyegarkan kurir");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Gagal menyegarkan daftar kurir")); } finally { setRefreshing(false); }
  };
  const isServiceOn = (c, svc) => !(servicesDisabled[c] || []).includes(svc);
  const toggleService = (c, svc) => setServicesDisabled((d) => {
    const cur = d[c] || [];
    const next = cur.includes(svc) ? cur.filter((x) => x !== svc) : [...cur, svc];
    const out = { ...d, [c]: next };
    if (!next.length) delete out[c];
    return out;
  });
  const set = (k) => (e) => setOrigin((o) => ({ ...o, [k]: e.target.value }));
  const toggle = (c) => setCouriers((l) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/biteship/settings", { origin, couriers, enabled, services_disabled: servicesDisabled, cod_enabled: enabled && codEnabled }); onSaved(data); toast.success("Pengaturan Biteship tersimpan."); }
    catch (e) { toast.error(errMsg(e, "Gagal menyimpan")); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="biteship-origin-card">
      <div className="flex items-center gap-2 font-semibold"><Truck className="h-4 w-4" /> Alamat Asal &amp; Kurir</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Nama pengirim/toko</Label><Input value={origin.name} onChange={set("name")} data-testid="biteship-origin-name" /></div>
        <div><Label>Telepon</Label><Input value={origin.phone} onChange={set("phone")} data-testid="biteship-origin-phone" /></div>
        <div className="sm:col-span-2"><Label>Alamat lengkap</Label><Input value={origin.address} onChange={set("address")} data-testid="biteship-origin-address" /></div>
        <div><Label>Kode pos</Label><Input value={origin.postal_code} onChange={set("postal_code")} data-testid="biteship-origin-postal" /></div>
        <div><Label>Email (opsional)</Label><Input value={origin.email || ""} onChange={set("email")} /></div>
      </div>
      <div>
        <Label>Titik lokasi (diperlukan kurir instan/same-day)</Label>
        <LocationPicker value={origin.coordinate || null} onChange={(v) => setOrigin((o) => ({ ...o, coordinate: v || "" }))} height={220} />
        {origin.coordinate && <p className="text-xs text-muted-foreground mt-1" data-testid="biteship-origin-coordinate">{origin.coordinate}</p>}
      </div>
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Label>Kurir aktif</Label>
          {cfg.configured && (
            <Button type="button" size="sm" variant="ghost" onClick={refreshCouriers} disabled={refreshing} data-testid="biteship-refresh-couriers-btn">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />Segarkan kurir
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="biteship-courier-count">
          {account.length ? `${account.length} kurir tersedia di akun Biteship Anda.` : "Daftar kurir akun belum tersinkron — klik Uji Koneksi / Segarkan kurir."}
        </p>
        <div className="flex flex-wrap gap-2 mt-1">
          {choices.map((c) => {
            const missing = account.length > 0 && !account.includes(c);
            return (
              <button type="button" key={c} onClick={() => toggle(c)} data-testid={`biteship-courier-${c}`}
                title={missing ? "Tersimpan, tetapi saat ini tidak tersedia di akun Biteship Anda" : undefined}
                className={`px-3 py-1 rounded-full border text-sm transition-colors ${couriers.includes(c) ? "bg-primary text-primary-foreground" : "bg-background"} ${missing ? "border-dashed opacity-70" : ""}`}>
                {c}{missing ? " *" : ""}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-1" data-testid="biteship-courier-hint">
          Tidak ada yang dipilih = semua kurir akun dipakai di checkout. {account.length > 0 && choices.some((c) => !account.includes(c)) ? "* = tersimpan tetapi tidak ada di akun Biteship saat ini." : ""}
        </p>
        {couriers.length > 0 && services && (
          <div className="mt-3 space-y-2" data-testid="biteship-services-section">
            <p className="text-xs text-muted-foreground">Layanan per kurir (kurir = saklar utama; layanan nonaktif tidak muncul di checkout):</p>
            {couriers.map((c) => (
              <div key={c} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`biteship-services-${c}`}>
                <span className="font-semibold w-24 uppercase text-xs">{c}</span>
                {servicesOf(c).length === 0 && <span className="text-xs text-muted-foreground">tidak ada data layanan</span>}
                {servicesOf(c).map((svc) => (
                  <label key={svc.courier_service_code} className="flex items-center gap-1 text-xs border rounded-full px-2 py-0.5">
                    <input type="checkbox" checked={isServiceOn(c, svc.courier_service_code)} onChange={() => toggleService(c, svc.courier_service_code)}
                      data-testid={`biteship-service-${c}-${svc.courier_service_code}`} />
                    {svc.courier_service_name || svc.courier_service_code}
                    {(svc.available_collection_method || []).filter((m) => CAP_LABEL[m]).map((m) => (
                      <span key={m} className="rounded bg-muted px-1 text-[10px] uppercase tracking-wide" data-testid={`biteship-service-cap-${c}-${svc.courier_service_code}-${m}`}>{CAP_LABEL[m]}</span>
                    ))}
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!cfg.configured} data-testid="biteship-enabled-toggle" />
        Aktifkan Biteship untuk ongkir toko ini (perlu API key tersimpan)
      </label>
      <label className="flex items-start gap-2 text-sm" data-testid="biteship-cod-row">
        <input type="checkbox" className="mt-0.5" checked={codEnabled} onChange={(e) => setCodEnabled(e.target.checked)} disabled={!cfg.configured || !enabled} data-testid="biteship-cod-enabled-toggle" />
        <span>Izinkan COD (Cash on Delivery) Biteship untuk toko ini
          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide" data-testid="biteship-cod-phase-badge">belum aktif di checkout</span>
          <span className="block text-xs text-muted-foreground">Fondasi Phase 1 — pelanggan belum bisa memilih COD; status dana COD tidak otomatis dianggap lunas.</span>
        </span>
      </label>
      <Button onClick={save} disabled={busy} data-testid="biteship-save-origin-btn">Simpan Pengaturan</Button>
    </Card>
  );
};

/* Webhook inbound Biteship — Headers Signature Key (nama header) + Secret (write-only) sesuai Dashboard Biteship. */
function WebhookCard({ cfg, onSaved }) {
  const [secret, setSecret] = useState("");
  const [header, setHeader] = useState(cfg.webhook_signature_header || "X-Biteship-Signature");
  const [busy, setBusy] = useState(false);
  const url = `${window.location.origin}${cfg.webhook_path || "/api/biteship/webhook"}`;
  const save = async () => {
    setBusy(true);
    try {
      const body = { webhook_signature_header: header };
      if (secret.trim()) body.webhook_secret = secret.trim();
      const { data } = await api.put("/admin/biteship/settings", body); setSecret(""); onSaved(data); toast.success("Pengaturan webhook tersimpan.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="biteship-webhook-card">
      <div className="font-semibold">Webhook Status Pengiriman (Biteship → Dynicty)</div>
      <p className="text-sm text-muted-foreground">Di Dashboard Biteship → Webhook, buat webhook untuk event <code>order.status</code> dengan URL di bawah, lalu isi <b>Headers Signature Key</b> (nama header) dan <b>Headers Signature Secret</b> yang sama seperti di sini. Secret disimpan terenkripsi dan tidak ditampilkan kembali.</p>
      <div className="text-sm">URL webhook: <code className="font-mono text-xs break-all" data-testid="biteship-webhook-url">{url}</code></div>
      <div className="text-sm" data-testid="biteship-webhook-status">Status: <span className="font-medium">{cfg.webhook_configured ? "secret tersimpan" : "belum dikonfigurasi"}</span>
        {cfg.webhook_secret_masked && <span className="ml-2 font-mono text-xs" data-testid="biteship-webhook-secret-masked">{cfg.webhook_secret_masked}</span>}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label htmlFor="biteship-webhook-header">Headers Signature Key (nama header)</Label>
          <Input id="biteship-webhook-header" value={header} onChange={(e) => setHeader(e.target.value)} data-testid="biteship-webhook-header-input" /></div>
        <div className="space-y-1"><Label htmlFor="biteship-webhook-secret">{cfg.webhook_configured ? "Ganti Signature Secret" : "Headers Signature Secret"}</Label>
          <Input id="biteship-webhook-secret" type="password" autoComplete="off" placeholder="min. 16 karakter" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="biteship-webhook-secret-input" /></div>
      </div>
      <Button onClick={save} disabled={busy} data-testid="biteship-webhook-save-btn">Simpan Webhook</Button>
    </Card>
  );
}

export default function BiteshipSettings() {
  const [cfg, setCfg] = useState(null);
  const load = async (data) => {
    if (data) return setCfg(data);
    try { const r = await api.get("/admin/biteship/settings"); setCfg(r.data); } catch (e) { toast.error(errMsg(e, "Gagal memuat pengaturan Biteship")); }
  };
  useEffect(() => { load(); }, []);
  return (
    <AdminLayout title="Biteship Shipping">
      <div className="max-w-3xl space-y-6" data-testid="admin-biteship-settings">
        <p className="text-sm text-muted-foreground">Integrasi Biteship untuk pesanan website (BYOC — Bring Your Own Biteship Account). Untuk memakai ongkir Biteship, set <code>shipping.provider</code> ke <code>biteship</code> di Shipping Settings setelah koneksi aktif.</p>
        {cfg ? (<><CredentialCard cfg={cfg} onSaved={load} /><WebhookCard cfg={cfg} onSaved={load} /><OriginCard cfg={cfg} onSaved={load} /></>) : <p data-testid="biteship-loading">Memuat…</p>}
      </div>
    </AdminLayout>
  );
}
